import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';

import Message from 'components/Message';
import { Table } from 'components/Table';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';

import { intlMessage } from 'constants/propTypes';
import { ICONS } from 'constants/icons';

import { PAGE_SIZE } from './SkuPayoutReports.constants';
import { mapLineItemToOrderRow } from './SkuPayoutReports.helpers';
import { skuPayoutReportsMessages as messages } from './SkuPayoutReports.messages';

const Th = ({ message, className }) => (
  <th className={className}>
    <Message className="text--medium text--sm" message={message} />
  </th>
);

Th.propTypes = { message: intlMessage.isRequired, className: PropTypes.string };

const Header = () => (
  <tr>
    <Th message={messages.tableHeaderOrderId} />
    <th className="text--right">
      <Message className="text--medium text--sm" message={messages.tableHeaderAmount} />
      <TooltipToggle name="sku-payout-orders-amount-mobile" className="sku-payout-orders-table__info-icon">
        <Isvg src={ICONS.infoGrey} />
      </TooltipToggle>
      <TooltipContent
        name="sku-payout-orders-amount-mobile"
        place="top"
        className="sku-payout-orders-table__tooltip-content">
        <Message message={messages.amountTooltip} />
      </TooltipContent>
    </th>
  </tr>
);

const payoutLineItemShape = PropTypes.shape({
  orderId: PropTypes.string,
  reservationId: PropTypes.number,
  adjustmentId: PropTypes.number,
});

const OrderRow = ({ row, onRowClick }) => {
  const handleClick = () => onRowClick(row.raw);
  const handleKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick(row.raw);
    }
  };

  return (
    <tr
      className="sku-payout-orders-table__row"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}>
      <td className="sku-payout-orders-table-mobile__order-id">{row.id}</td>
      <td className="text--right">
        <span className="sku-payout-orders-table-mobile__amount-cell">
          <Message format="currency" value={row.amount || 0} currency={row.currencyCode} />
          <Isvg className="sku-payout-orders-table-mobile__chevron" src={ICONS.chevronRightThin} />
        </span>
      </td>
    </tr>
  );
};

OrderRow.propTypes = {
  row: PropTypes.shape({
    key: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    amount: PropTypes.number,
    currencyCode: PropTypes.string,
    raw: payoutLineItemShape.isRequired,
  }).isRequired,
  onRowClick: PropTypes.func.isRequired,
};

const SkuPayoutOrdersTableMobile = ({ rows, venueTimezone, isLoading, page, totalItems, onChangePage, onRowClick }) => {
  const normalizedRows = useMemo(
    () => (rows || []).map(lineItem => mapLineItemToOrderRow(lineItem, { venueTimezone })).filter(Boolean),
    [rows, venueTimezone]
  );

  return (
    <Table
      className="sku-payout-orders-table sku-payout-orders-table-mobile"
      emptyMessage={messages.emptyOrders}
      header={<Header />}
      isLoading={isLoading}
      hasBottomPageTurner
      changePage={onChangePage}
      count={totalItems}
      entriesPerPage={PAGE_SIZE}
      page={page}>
      {normalizedRows.map(row => (
        <OrderRow key={row.key} row={row} onRowClick={onRowClick} />
      ))}
    </Table>
  );
};

SkuPayoutOrdersTableMobile.propTypes = {
  rows: PropTypes.arrayOf(payoutLineItemShape),
  venueTimezone: PropTypes.string,
  isLoading: PropTypes.bool,
  page: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
};

export default SkuPayoutOrdersTableMobile;
